import {useQueryClient, type QueryClient} from "@tanstack/react-query";
import {EventContainerListDispositionSidePanel} from "~/components/eventContainerDetails/eventContainerListDispositionSidePanel";
import {useWorkspace} from "~/contexts/workspaceContext";
import type {AmaronDispositionKanbanViewLevel} from "~/constants/amaronDispositionMapping";
import {AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID, amaronDispositionConfig} from "~/domainModels/workspaceSpecific/amaron";
import type {OttopilotTriggerConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import {
    applyAmaronDispositionTrackerJourneyDispositionUpdate,
    invalidateAmaronDispositionKanbanDatasetQueries,
} from "~/utilities/generateAmaronDispositionKanbanData";

function getAmaronLeadDisplayName(event: EventWithEventContainer): string {
    const raw = event.eventContainer.additionalColumnValues[AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID];
    if (typeof raw === "string" && raw.trim() !== "") {
        return raw.trim();
    }
    return "Lead";
}

export function AmaronLeadDispositionModal(props: {
    onClose: () => void;
    event: EventWithEventContainer;
    eventContainerTemplate: EventContainerTemplate;
    triggerConfiguration: OttopilotTriggerConfig;
    queryKeyPrefix: Array<string | null>;
    dispositionLevel?: AmaronDispositionKanbanViewLevel;
    kanbanColumns?: ReadonlyArray<{id: string; slug: string}>;
    onRefreshStateChange?: (isRefreshing: boolean) => void;
    invalidateDataViewerQueries?: (queryClient: QueryClient, queryKeyPrefix: ReadonlyArray<string | null>) => Promise<void>;
}) {
    const queryClient = useQueryClient();
    const {workspace} = useWorkspace();
    const usesCustomDataViewerInvalidation = props.invalidateDataViewerQueries !== undefined;
    const canOptimisticallyUpdateKanban =
        props.dispositionLevel !== undefined && props.kanbanColumns !== undefined && usesCustomDataViewerInvalidation !== true;

    return (
        <EventContainerListDispositionSidePanel
            {...props}
            dispositionConfiguration={amaronDispositionConfig}
            // Amaron leaf dispositions are underscore-separated and can be L1–L4
            // (e.g. `connected_valid_interested_follow-up`), so the selector must expose L4.
            dispositionUiLevels={4}
            getLeadDisplayName={getAmaronLeadDisplayName}
            invalidateQueriesBeforeDataViewer={async () => {
                await invalidateAmaronDispositionKanbanDatasetQueries(queryClient, workspace.id);
            }}
            onDispositionSaved={
                canOptimisticallyUpdateKanban === true
                    ? ({eventContainerId, dispositionId}) => {
                          applyAmaronDispositionTrackerJourneyDispositionUpdate({
                              queryClient,
                              workspaceId: workspace.id,
                              queryKeyPrefix: props.queryKeyPrefix,
                              journeyId: eventContainerId,
                              newDispositionId: dispositionId,
                              viewLevel: props.dispositionLevel!,
                              kanbanColumns: props.kanbanColumns!,
                              eventContainerTemplate: props.eventContainerTemplate,
                          });
                      }
                    : undefined
            }
            refetchDataViewerAfterSave={canOptimisticallyUpdateKanban === false}
            invalidateDataViewerQueries={props.invalidateDataViewerQueries}
        />
    );
}
